import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import type { StaffRole } from '@/api/types';
import { Spinner } from '@/ui';

import { homePathForRole, useAuth } from './context';

/**
 * The route guard.
 *
 * It redirects; it does not render an empty page. A driver who follows a
 * bookmarked /admin/orders link lands on their own day rather than on a blank
 * shell that never resolves, and someone whose session expired mid-shift is
 * sent to /login with the path they wanted, so signing back in returns them to
 * it instead of to the dashboard.
 *
 * This is a courtesy, not a control. Every handler behind it checks the role
 * and the ownership from the verified token — see http/staffAuth.ts — so the
 * worst a tampered client achieves is drawing a button that answers 403.
 */

interface RequireRoleProps {
  /** Omitted means "any signed-in staff member", which is what /driver/* wants. */
  role?: StaffRole | StaffRole[];
  /** Falls back to <Outlet /> so it can be used as a layout route. */
  children?: ReactNode;
}

export function RequireRole({ role, children }: RequireRoleProps) {
  const { status, staff } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return <Spinner center label="Checking your session" />;
  }

  if (status === 'anonymous' || staff === null) {
    // `from` is a location, not a full URL: an attacker-supplied absolute URL in
    // a redirect parameter is an open redirect, and router state cannot be one.
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  const allowed = role === undefined || (Array.isArray(role) ? role : [role]).includes(staff.role);

  if (!allowed) {
    return <Navigate to={homePathForRole(staff.role)} replace />;
  }

  return <>{children ?? <Outlet />}</>;
}
